(() => {
  const AuremeCheckoutUtils = window.AuremeCheckoutUtils;
  /* 配送希望日は当日を起点に 5〜9 日後の範囲だけ選択可能とする業務ルール */
  const AUREME_DELIVERY_DATE_START_DAY = 5;
  const AUREME_DELIVERY_DATE_END_DAY = 9;
  const AUREME_POINTS_APPLY_LABEL = 'ポイントを利用する';
  const AUREME_POINTS_CLEAR_LABEL = 'ポイントクリア';
  const AUREME_POINTS_APPLY_REQUIRED_MESSAGE =
    '「ポイントを利用する」ボタンを押してください。<br>ポイントを利用しない場合、今回利用するポイント数に「0」をご入力ください。';
  const AUREME_POINTS_SYSTEM_ERROR_MESSAGE =
    'システムエラーが発生しました。\nページを再読み込みしてください。解決しない場合には、三陽商会ECカスタマーサポートへお問い合わせください。';
  const AUREME_POINT_GIFT_CARD_ATTRIBUTE = 'ギフトカードコード';
  const AUREME_CHECKOUT_CONTEXT_TTL = 2 * 60 * 60 * 1000;
  const AUREME_CHECKOUT_CONTEXT_READY_FLAG = AuremeCheckoutUtils.CHECKOUT_READY_FLAG;
  const AUREME_CART_REFERRER_KEY = 'auremeCartReferrer';
  const AUREME_ALL_COLLECTION_PATH = '/collections/all';
  const AUREME_CART_CONTINUE_SHOPPING_FALLBACK_PATH = '/collections/all-products';
  const AUREME_CART_ITEM_LIMIT = AuremeCheckoutUtils.CART_ITEM_LIMIT;
  const AUREME_DELIVERY_APP_WRAPPER_SELECTOR = '[data-aureme-cart-delivery-app]';
  const AUREME_DELIVERY_APP_LOADING_SELECTOR = '[data-aureme-cart-delivery-loading]';
  const AUREME_DELIVERY_APP_READY_SELECTOR = '.delivery_box';
  const auremeDeliveryAppLoadingObservers = new Map();

  function normalizeAuremeInt(value) {
    return Math.max(0, parseInt(value || 0, 10) || 0);
  }

  function getAuremeContinueShoppingUrl() {
    const configuredUrl = window.Aureme?.config?.cartContinueShoppingUrl;
    return typeof configuredUrl === 'string' && configuredUrl
      ? configuredUrl
      : AUREME_CART_CONTINUE_SHOPPING_FALLBACK_PATH;
  }

  function normalizeAuremeContinueShoppingUrl(url) {
    if (!url) return url;

    try {
      const parsedUrl = new URL(url, window.location.origin);
      const pathname = parsedUrl.pathname.replace(/\/$/, '') || '/';

      if (parsedUrl.origin === window.location.origin && pathname === AUREME_ALL_COLLECTION_PATH) {
        return `${getAuremeContinueShoppingUrl()}${parsedUrl.search}${parsedUrl.hash}`;
      }
    } catch {
      /* 壊れた保存値や sandbox 由来で URL 生成に失敗した場合も、既知の相対パスだけ補正する */
      if (url === AUREME_ALL_COLLECTION_PATH) return getAuremeContinueShoppingUrl();
    }

    return url;
  }

  const jaNumberFormat = new Intl.NumberFormat('ja-JP');

  function formatAuremeYen(value) {
    return `¥${jaNumberFormat.format(normalizeAuremeInt(value))}`;
  }

  function formatAuremeNumber(value) {
    return jaNumberFormat.format(normalizeAuremeInt(value));
  }

  const AUREME_POINT_USE_MAX = 1000000;

  function getAuremeCustomerIdString() {
    if (typeof AUREME_CUSTOMER_ID === 'undefined' || AUREME_CUSTOMER_ID === null) return '';
    return String(AUREME_CUSTOMER_ID).trim();
  }

  /** sanyo/crest 同様、利用ポイント数は LS に ja-JP 区切りで保持される */
  function readAuremeAppliedPointsFromLocalStorage(customerId) {
    if (!customerId) return 0;
    const raw = (localStorage.getItem(`sanyo_app_use_point_${customerId}`) || '').trim();
    return normalizeAuremeInt(parseInt(raw.replace(/,/g, ''), 10));
  }

  function auremePointGiftCardActive() {
    return (document.querySelector('[data-cart-points-giftcard]')?.value || '').trim() !== '';
  }

  /** App Proxy 利用後は localStorage にギフトコードが入る。data-cart-balance がまだ 0 の間も適用状態を維持するために使う */
  function auremePointGiftReservedInStorage(customerId) {
    if (!customerId) return false;
    return !!(localStorage.getItem(`sanyo_app_gift_card_code_${customerId}`) || '').trim();
  }

  function auremePointReservationActive() {
    return (
      auremePointGiftCardActive() || auremePointGiftReservedInStorage(getAuremeCustomerIdString())
    );
  }

  /* cart.js 反映時に必要なポイント同期値を先に算出し、DOM 更新側の分岐を増やさない */
  function deriveAuremePointSyncState(cart, appliedFromDom) {
    const attrs = cart.attributes || {};
    const hasCartGiftAttribute = Object.hasOwn(attrs, AUREME_POINT_GIFT_CARD_ATTRIBUTE);
    const cartGift = attrs[AUREME_POINT_GIFT_CARD_ATTRIBUTE];
    const cartGiftString = cartGift != null && cartGift !== false ? String(cartGift) : '';
    const cartGiftValue = cartGiftString.trim();
    const hasCartGiftCode = cartGiftValue !== '';
    const customerId = getAuremeCustomerIdString();
    const appliedFromStorage = readAuremeAppliedPointsFromLocalStorage(customerId);
    const pointDiscountYen = hasCartGiftCode
      ? AuremeCheckoutUtils.getPointDiscountAmountYen(cart, cartGiftValue)
      : 0;
    const hasPointReservation = hasCartGiftCode && auremePointReservationActive();
    const shouldKeepPointValue = pointDiscountYen > 0 || hasPointReservation;
    let syncedAppliedYen = 0;
    if (pointDiscountYen > 0) {
      syncedAppliedYen = pointDiscountYen;
    } else if (shouldKeepPointValue) {
      syncedAppliedYen = Math.max(appliedFromDom, appliedFromStorage);
    }

    const totalCents = Number(cart.total_price);
    const totalYen = Math.max(0, Math.round(totalCents / 100));
    /*
     * total_price にギフト（ポイント）分が織り込まれたあと data-cart-total を更新すると、
     * updateAuremeTotalDisplay が再度 DOM/localStorage の利用ポイントを引き、表示が二重に減る。
     */
    const cartTotalBaseYen = pointDiscountYen > 0 ? totalYen + pointDiscountYen : totalYen;

    return {
      hasCartGiftAttribute,
      cartGiftString,
      shouldKeepPointValue,
      syncedAppliedYen,
      cartTotalBaseYen,
    };
  }

  function auremePointErrorFlagIsSet(errorFlg) {
    if (errorFlg === undefined || errorFlg === null) return false;
    return Number(errorFlg) !== 0;
  }

  function createAuremePointApiMessageError(message) {
    const error = new Error(message);
    error.auremePointApiMessage = true;
    return error;
  }

  function resolveAuremePointsAlertMessage(error) {
    if (
      error &&
      typeof error === 'object' &&
      error.auremePointApiMessage === true &&
      typeof error.message === 'string' &&
      error.message
    ) {
      return error.message;
    }
    return AUREME_POINTS_SYSTEM_ERROR_MESSAGE;
  }

  async function fetchAuremePointUseApi(usePointInt) {
    const params = new URLSearchParams({ usePoint: String(usePointInt) });
    const url = `https://${AUREME_STORE_DOMAIN}${SANYO_APP_PROXY_PATH}/point/use?${params.toString()}`;
    let response;
    try {
      response = await fetch(url, { method: 'POST' });
    } catch {
      throw new Error(AUREME_POINTS_SYSTEM_ERROR_MESSAGE);
    }
    if (!response.ok) {
      throw new Error(AUREME_POINTS_SYSTEM_ERROR_MESSAGE);
    }
    let json;
    try {
      json = await response.json();
    } catch {
      throw new Error(AUREME_POINTS_SYSTEM_ERROR_MESSAGE);
    }
    if (auremePointErrorFlagIsSet(json.error_flg)) {
      if (json.error_msg) throw createAuremePointApiMessageError(json.error_msg);
      throw new Error(AUREME_POINTS_SYSTEM_ERROR_MESSAGE);
    }
    if (!json.giftCardCode) {
      throw new Error(AUREME_POINTS_SYSTEM_ERROR_MESSAGE);
    }
    return String(json.giftCardCode);
  }

  async function fetchAuremePointCancelApi(giftCardCode, usePointFormatted) {
    const params = new URLSearchParams({
      giftCardCode: String(giftCardCode),
      usePoint:
        usePointFormatted != null && usePointFormatted !== '' ? String(usePointFormatted) : '',
    });
    const url = `https://${AUREME_STORE_DOMAIN}${SANYO_APP_PROXY_PATH}/point/cancel?${params.toString()}`;
    let response;
    try {
      response = await fetch(url, { method: 'POST' });
    } catch {
      throw new Error(AUREME_POINTS_SYSTEM_ERROR_MESSAGE);
    }
    if (!response.ok) {
      throw new Error(AUREME_POINTS_SYSTEM_ERROR_MESSAGE);
    }
    let json;
    try {
      json = await response.json();
    } catch {
      throw new Error(AUREME_POINTS_SYSTEM_ERROR_MESSAGE);
    }
    if (auremePointErrorFlagIsSet(json.error_flg)) {
      throw new Error(AUREME_POINTS_SYSTEM_ERROR_MESSAGE);
    }
  }

  /** cart/update.js のレスポンス等からトークンを保存（syncAuremePointUsingStateFromApi より先に必須） */
  function persistAuremePointCartSnapshotFromCartJson(cart, customerIdOverride) {
    const customerId = customerIdOverride || getAuremeCustomerIdString();
    if (!customerId || !cart || typeof cart.token !== 'string' || cart.token === '') return;
    try {
      localStorage.setItem(`sanyo_app_cart_token_${customerId}`, cart.token);
      localStorage.setItem(`sanyo_app_point_use_date_${customerId}`, String(Date.now()));
      if (cart.items_subtotal_price != null && !Number.isNaN(Number(cart.items_subtotal_price))) {
        localStorage.setItem(
          `sanyo_app_subtotal_price_${customerId}`,
          String(Number(cart.items_subtotal_price) / 100),
        );
      }
    } catch {
      /* quota */
    }
  }

  async function persistAuremeCartTokenForPoints(customerId) {
    const data = await fetch(`${routes.cart_url}.js`).then((response) => {
      if (!response.ok) throw new Error('cart.js failed');
      return response.json();
    });
    persistAuremePointCartSnapshotFromCartJson(data, customerId);
  }

  function buildAuremeDeliveryDateOptions(select) {
    if (!select || select.dataset.initialized === 'true') return;

    const selectedValue = select.dataset.selected || '';
    const options = ['<option value="">指定なし</option>'];
    const baseDate = new Date();

    for (
      let index = AUREME_DELIVERY_DATE_START_DAY;
      index <= AUREME_DELIVERY_DATE_END_DAY;
      index += 1
    ) {
      const optionDate = new Date(baseDate);
      optionDate.setDate(baseDate.getDate() + index);

      const year = optionDate.getFullYear();
      const month = `${optionDate.getMonth() + 1}`.padStart(2, '0');
      const day = `${optionDate.getDate()}`.padStart(2, '0');
      const optionValue = `${year}-${month}-${day}`;

      options.push(`<option value="${optionValue}">${optionValue}</option>`);
    }

    select.innerHTML = options.join('');

    if (selectedValue && [...select.options].some((option) => option.value === selectedValue)) {
      select.value = selectedValue;
    }

    select.dataset.initialized = 'true';
  }

  /* カート購入制限表示と checkout ボタン制御をまとめる */
  const auremeCartRestrictionHelpers = {
    getState(cartItemsElement) {
      let preorderQuantity = 0;
      let regularQuantity = 0;

      for (const item of cartItemsElement.querySelectorAll('.cart-item[data-aureme-item-kind]')) {
        const qty = normalizeAuremeInt(item.dataset.auremeQuantity);
        if (item.dataset.auremeItemKind === 'preorder') {
          preorderQuantity += qty;
        } else {
          regularQuantity += qty;
        }
      }

      const hasPreorderItems = preorderQuantity > 0;
      const hasRegularItems = regularQuantity > 0;
      const hasMultiplePreorderItems = preorderQuantity > 1;
      const isMixedCart = hasPreorderItems && hasRegularItems;
      const itemCount = preorderQuantity + regularQuantity;
      const isCartItemLimitExceeded = itemCount >= AUREME_CART_ITEM_LIMIT;

      return {
        preorderQuantity,
        regularQuantity,
        itemCount,
        hasPreorderItems,
        hasRegularItems,
        hasMultiplePreorderItems,
        isCartItemLimitExceeded,
        isMixedCart,
        isCheckoutBlocked: hasMultiplePreorderItems || isMixedCart || isCartItemLimitExceeded,
      };
    },

    sync(elements, state) {
      const isDisabled = state.itemCount === 0 || state.isCheckoutBlocked;

      const noticeVisibility = {
        noticesContainer: state.hasPreorderItems || state.isCartItemLimitExceeded,
        deliveryNotice: state.hasPreorderItems,
        mixedNotice: state.isMixedCart,
        multipleNotice: state.hasMultiplePreorderItems,
        limitNotice: state.isCartItemLimitExceeded,
      };

      for (const [key, shouldShow] of Object.entries(noticeVisibility)) {
        if (elements[key]) elements[key].hidden = !shouldShow;
      }

      for (const button of [elements.checkoutButton, elements.drawerCheckoutButton]) {
        if (button) button.disabled = isDisabled;
      }
    },

    focusNotice(elements) {
      const { noticesContainer, deliveryNotice, mixedNotice, multipleNotice, limitNotice } =
        elements;
      const focusTarget = [
        limitNotice,
        mixedNotice,
        multipleNotice,
        deliveryNotice,
        noticesContainer,
      ].find((element) => element && !element.hidden);

      if (!focusTarget) return false;

      focusTarget.setAttribute('tabindex', '-1');
      focusTarget.scrollIntoView({ behavior: 'smooth', block: 'center' });
      focusTarget.focus({ preventScroll: true });
      return true;
    },
  };

  /*
   * checkout 直前に保存した cart token と戻り表示用のクーポン notice を突合する。
   * 別カート・期限切れ・対象 notice なしの場合は通常割引表示へ戻し、古い sessionStorage を残さない。
   */
  const auremeCheckoutCouponHelpers = {
    readContext() {
      try {
        const rawContext = sessionStorage.getItem(AuremeCheckoutUtils.CHECKOUT_CONTEXT_KEY);
        if (!rawContext) return null;

        const parsedContext = JSON.parse(rawContext);
        if (
          !parsedContext ||
          typeof parsedContext.token !== 'string' ||
          parsedContext.token === '' ||
          typeof parsedContext.recordedAt !== 'number'
        ) {
          AuremeCheckoutUtils.clearCheckoutContext();
          return null;
        }

        return parsedContext;
      } catch (error) {
        console.error(error);
        AuremeCheckoutUtils.clearCheckoutContext();
        return null;
      }
    },

    isExpired(checkoutContext) {
      if (!checkoutContext) return true;
      return Date.now() - checkoutContext.recordedAt > AUREME_CHECKOUT_CONTEXT_TTL;
    },

    toggleDisplay(shouldShow, elements) {
      const { itemNoticeLists, itemDefaultDiscountLists } = elements;

      /* クーポン notice と既定の割引表示は同じ割引を二重に見せないよう、どちらか一方だけを表示する */
      let hasVisibleItemNotices = false;

      for (const itemNoticeList of itemNoticeLists) {
        const hasNoticeItems =
          itemNoticeList.querySelector('[data-cart-item-checkout-coupon-notice]') !== null;
        const shouldDisplayList = shouldShow && hasNoticeItems;
        itemNoticeList.hidden = !shouldDisplayList;
        hasVisibleItemNotices = hasVisibleItemNotices || shouldDisplayList;
      }

      for (const defaultDiscountList of itemDefaultDiscountLists) {
        defaultDiscountList.hidden = hasVisibleItemNotices;
      }
    },

    async sync(cartController) {
      if (!cartController.isAuremeCartPage()) return;

      const checkoutContext = auremeCheckoutCouponHelpers.readContext();
      if (!checkoutContext) {
        auremeCheckoutCouponHelpers.toggleDisplay(
          false,
          cartController.getAuremeCheckoutCouponElements(),
        );
        return;
      }

      if (auremeCheckoutCouponHelpers.isExpired(checkoutContext)) {
        AuremeCheckoutUtils.clearCheckoutContext();
        auremeCheckoutCouponHelpers.toggleDisplay(
          false,
          cartController.getAuremeCheckoutCouponElements(),
        );
        return;
      }

      try {
        const cartState = await AuremeCheckoutUtils.fetchCartState();
        const couponElements = cartController.getAuremeCheckoutCouponElements();
        const hasItemCheckoutCoupons = couponElements.itemNoticeItems.length > 0;

        if (
          !cartState?.token ||
          cartState.token !== checkoutContext.token ||
          !hasItemCheckoutCoupons
        ) {
          AuremeCheckoutUtils.clearCheckoutContext();
          auremeCheckoutCouponHelpers.toggleDisplay(false, couponElements);
          return;
        }

        auremeCheckoutCouponHelpers.toggleDisplay(true, couponElements);
      } catch (error) {
        console.error(error);
        /* クーポン情報取得失敗をユーザーに通知 */
        window.AuremeToast?.show({
          message: 'クーポン情報の取得に失敗しました。\nページを再読み込みしてお試しください。',
          type: 'error',
        });
        auremeCheckoutCouponHelpers.toggleDisplay(
          false,
          cartController.getAuremeCheckoutCouponElements(),
        );
      }
    },
  };

  /*
   * 「配送日時指定.amp」は本体を非同期で挿入するため、
   * theme 側の loading は app の ready marker を検出した時点でだけ閉じる。
   */
  const auremeDeliveryAppLoadingHelpers = {
    disconnect(wrapper) {
      const observer = auremeDeliveryAppLoadingObservers.get(wrapper);
      if (!observer) return;

      observer.disconnect();
      auremeDeliveryAppLoadingObservers.delete(wrapper);
    },

    hide(wrapper, loadingElement) {
      loadingElement.hidden = true;
      auremeDeliveryAppLoadingHelpers.disconnect(wrapper);
    },

    show(wrapper, loadingElement) {
      loadingElement.hidden = false;

      if (
        auremeDeliveryAppLoadingObservers.has(wrapper) ||
        typeof MutationObserver === 'undefined'
      ) {
        return;
      }

      const observer = new MutationObserver(() => {
        if (!wrapper.isConnected) {
          auremeDeliveryAppLoadingHelpers.disconnect(wrapper);
          return;
        }

        if (!wrapper.querySelector(AUREME_DELIVERY_APP_READY_SELECTOR)) return;

        auremeDeliveryAppLoadingHelpers.hide(wrapper, loadingElement);
      });

      observer.observe(wrapper, { childList: true, subtree: true });
      auremeDeliveryAppLoadingObservers.set(wrapper, observer);
    },

    cleanup() {
      for (const wrapper of auremeDeliveryAppLoadingObservers.keys()) {
        if (wrapper.isConnected) continue;

        auremeDeliveryAppLoadingHelpers.disconnect(wrapper);
      }
    },

    sync() {
      auremeDeliveryAppLoadingHelpers.cleanup();

      for (const wrapper of document.querySelectorAll(AUREME_DELIVERY_APP_WRAPPER_SELECTOR)) {
        const loadingElement = wrapper.querySelector(AUREME_DELIVERY_APP_LOADING_SELECTOR);
        if (!loadingElement) continue;

        if (wrapper.querySelector(AUREME_DELIVERY_APP_READY_SELECTOR)) {
          auremeDeliveryAppLoadingHelpers.hide(wrapper, loadingElement);
          continue;
        }

        auremeDeliveryAppLoadingHelpers.show(wrapper, loadingElement);
      }
    },
  };

  const auremeCartMethods = {
    updateAuremeCartPageEmptyState(itemCount) {
      if (!this.isAuremeCartPage()) return;

      document.body.classList.toggle('cart-page--empty', itemCount === 0);
    },

    /* AUREMEのカート購入制限状態を checkout 前の制御に使う */
    getAuremeCartRestrictionState() {
      return auremeCartRestrictionHelpers.getState(this);
    },

    getAuremeCartElements() {
      if (this._auremeCartElements) {
        return this._auremeCartElements;
      }

      const totalsElement = document.querySelector('[data-cart-totals]');
      this._auremeCartElements = {
        totalsElement,
        totalValueElement: document.querySelector('[data-cart-total-value]'),
        cartTotal: parseInt(totalsElement?.dataset.cartTotal || '0', 10) || 0,
        pointsBalance: parseInt(totalsElement?.dataset.cartBalance || '0', 10) || 0,
        pointsBalanceElement: document.querySelector('.aureme-cart-footer__points-balance'),
        pointsInput: document.querySelector('[data-cart-points-input]'),
        pointsHidden: document.querySelector('[data-cart-points-hidden]'),
        pointsError: document.querySelector('[data-cart-points-error]'),
        pointsApplyButton: document.querySelector('[data-cart-points-apply]'),
        noticesContainer: document.querySelector('[data-aureme-cart-notices]'),
        deliveryNotice: document.querySelector('[data-aureme-cart-notice="delivery"]'),
        mixedNotice: document.querySelector('[data-aureme-cart-notice="mixed"]'),
        multipleNotice: document.querySelector('[data-aureme-cart-notice="multiple"]'),
        limitNotice: document.querySelector('[data-aureme-cart-notice="limit"]'),
        cartForm: document.getElementById('cart'),
        checkoutButton:
          document.getElementById('aureme-cart-checkout-button') ||
          document.getElementById('checkout'),
        drawerForm: document.getElementById('CartDrawer-Form'),
        drawerCheckoutButton: document.getElementById('CartDrawer-Checkout'),
      };

      return this._auremeCartElements;
    },

    getAuremeFooterState() {
      if (this._auremeFooterState) {
        return this._auremeFooterState;
      }

      const footerStateElement = document.querySelector('[data-cart-footer-state]');
      const footerState = AuremeCheckoutUtils.parseFooterStateElement(footerStateElement);

      this._auremeFooterState = {
        footerStateElement,
        ...(footerState || {}),
      };

      return this._auremeFooterState;
    },

    isAuremeCartPage() {
      const currentPath = window.location.pathname.replace(/\/$/, '');
      const cartPath = routes.cart_url.replace(/\/$/, '');
      return currentPath === cartPath;
    },

    getAuremeCheckoutCouponElements() {
      if (this._auremeCheckoutCouponElements) {
        return this._auremeCheckoutCouponElements;
      }

      this._auremeCheckoutCouponElements = {
        itemNoticeLists: [...document.querySelectorAll('[data-cart-item-checkout-coupon-notices]')],
        itemNoticeItems: [...document.querySelectorAll('[data-cart-item-checkout-coupon-notice]')],
        itemDefaultDiscountLists: [
          ...document.querySelectorAll('[data-cart-item-discounts-default]'),
        ],
      };

      return this._auremeCheckoutCouponElements;
    },

    invalidateAuremeElementCache() {
      this._auremeCartElements = undefined;
      this._auremeFooterState = undefined;
      this._auremeCheckoutCouponElements = undefined;
    },

    readAuremeCheckoutContext() {
      return auremeCheckoutCouponHelpers.readContext();
    },

    isAuremeCheckoutContextExpired(checkoutContext) {
      return auremeCheckoutCouponHelpers.isExpired(checkoutContext);
    },

    async recordAuremeCheckoutContext() {
      try {
        /* prepareAuremeDeliveryAttributesForCheckoutで取得済みのカート状態を再利用 */
        const cartState = this._lastCartState || (await AuremeCheckoutUtils.fetchCartState());
        this._lastCartState = undefined;
        AuremeCheckoutUtils.recordCheckoutContext({ cartState, clearOnMissingToken: true });
      } catch (error) {
        console.error(error);
      }
    },

    toggleAuremeCheckoutCouponDisplay(shouldShow, preloadedElements) {
      auremeCheckoutCouponHelpers.toggleDisplay(
        shouldShow,
        preloadedElements || this.getAuremeCheckoutCouponElements(),
      );
    },

    /*
     * checkout 往復後だけ item 単位のクーポン注意を表示するため、
     * 直前に保存したカート token と現在の cart.js が一致する場合だけ既定の割引表示を差し替える。
     */
    async syncAuremeCheckoutCouponDisplay() {
      await auremeCheckoutCouponHelpers.sync(this);
    },

    resumeAuremeCheckoutSubmit(form, submitter) {
      AuremeCheckoutUtils.resumeCheckoutSubmit({
        form,
        submitter,
        readyFlag: AUREME_CHECKOUT_CONTEXT_READY_FLAG,
        /* 実 submit 時に submitter が欠けるケースでも checkout ボタンを補完する */
        getFallbackSubmitter: ({ form: connectedForm, submitter: connectedSubmitter }) =>
          connectedSubmitter ||
          connectedForm.querySelector('[name="checkout"]') ||
          document.querySelector(`[form="${connectedForm.id}"][name="checkout"]`),
        /*
         * 再送 submit が実行されないまま ready flag が残ると、次回のユーザー操作が
         * prepare 処理を飛ばして checkout へ抜けてしまうため、5 秒で必ず破棄する。
         */
        onReadyFlagSet: (connectedForm) => {
          if (this.auremeCheckoutReadyCleanupTimer) {
            window.clearTimeout(this.auremeCheckoutReadyCleanupTimer);
          }

          this.auremeCheckoutReadyCleanupTimer = window.setTimeout(() => {
            this.clearAuremeCheckoutReadyFlag(connectedForm);
          }, 5000);
        },
        onRequestSubmitError: (_error, connectedForm) => {
          this.clearAuremeCheckoutReadyFlag(connectedForm);
          window.AuremeToast?.show({
            message: 'ご購入手続きへ進めませんでした。\n時間をおいて再度お試しください。',
            type: 'error',
          });
        },
      });
    },

    clearAuremeCheckoutReadyFlag(form) {
      AuremeCheckoutUtils.clearCheckoutReadyFlag(form, AUREME_CHECKOUT_CONTEXT_READY_FLAG);
      if (this.auremeCheckoutReadyCleanupTimer) {
        window.clearTimeout(this.auremeCheckoutReadyCleanupTimer);
        this.auremeCheckoutReadyCleanupTimer = undefined;
      }
    },

    syncAuremeCartRestrictions() {
      const state = this.getAuremeCartRestrictionState();
      const elements = this.getAuremeCartElements();
      auremeCartRestrictionHelpers.sync(elements, state);

      return state;
    },

    focusAuremeCartRestrictionNotice() {
      return auremeCartRestrictionHelpers.focusNotice(this.getAuremeCartElements());
    },

    showAuremeCartItemLimitNotice() {
      const elements = this.getAuremeCartElements();

      if (elements.noticesContainer) elements.noticesContainer.hidden = false;
      if (elements.limitNotice) elements.limitNotice.hidden = false;

      for (const button of [elements.checkoutButton, elements.drawerCheckoutButton]) {
        if (button) button.disabled = true;
      }

      if (!this.focusAuremeCartRestrictionNotice() && !this.isAuremeCartPage()) {
        window.location.href = routes.cart_url;
      }
    },

    bindAuremeCheckoutGuard() {
      const { cartForm, checkoutButton, drawerForm, drawerCheckoutButton } =
        this.getAuremeCartElements();

      const guardCheckout = (event) => {
        const isNonCheckoutSubmit =
          event.type === 'submit' && event.submitter?.name && event.submitter.name !== 'checkout';
        if (isNonCheckoutSubmit) return;

        const submitForm = event.type === 'submit' ? event.currentTarget : null;
        const isPreparedSubmit =
          submitForm instanceof HTMLFormElement &&
          submitForm.dataset[AUREME_CHECKOUT_CONTEXT_READY_FLAG] === 'true';
        const state = this.syncAuremeCartRestrictions();

        if (!state.isCheckoutBlocked) {
          if (!this.validateAuremePointsBeforeCheckout()) {
            event.preventDefault();
            event.stopPropagation();
            if (!this.isAuremeCartPage()) {
              window.location.href = routes.cart_url;
            }
            return;
          }

          /*
           * checkout 用 attribute 補完後の再送 submit は ready flag で通し、
           * 同じ guard が二重に prepare 処理を走らせないようにする。
           */
          if (isPreparedSubmit) {
            this.clearAuremeCheckoutReadyFlag(submitForm);
            return;
          }
          if (event.type === 'submit') {
            const submitter = event.submitter;
            event.preventDefault();
            event.stopPropagation();
            AuremeCheckoutUtils.prepareAndResumeCheckoutSubmit({
              form: submitForm,
              onCartItemLimitExceeded: () => this.showAuremeCartItemLimitNotice(),
              prepareDeliveryAttributes: () => this.prepareAuremeDeliveryAttributesForCheckout(),
              recordContext: () => this.recordAuremeCheckoutContext(),
              resumeSubmit: () => this.resumeAuremeCheckoutSubmit(submitForm, submitter),
            });
          }
          return;
        }

        event.preventDefault();
        event.stopPropagation();

        if (!this.focusAuremeCartRestrictionNotice() && !this.isAuremeCartPage()) {
          window.location.href = routes.cart_url;
        }
      };

      const bindGuard = (element, eventType) => {
        if (!element || element.dataset.auremeCheckoutGuardBound === 'true') return;
        element.addEventListener(eventType, guardCheckout);
        element.dataset.auremeCheckoutGuardBound = 'true';
      };

      bindGuard(checkoutButton, 'click');
      bindGuard(drawerCheckoutButton, 'click');
      bindGuard(cartForm, 'submit');
      bindGuard(drawerForm, 'submit');
    },

    consumeAuremeCartReferrer() {
      try {
        const referrer = sessionStorage.getItem(AUREME_CART_REFERRER_KEY);
        if (referrer) sessionStorage.removeItem(AUREME_CART_REFERRER_KEY);
        return referrer;
      } catch {
        return null;
      }
    },

    applyAuremeContinueShoppingReferrer() {
      const source = this.tagName === 'CART-ITEMS' ? this : document.querySelector('cart-items');
      if (!source?.auremeCartReferrer) return;

      for (const continueShoppingButton of document.querySelectorAll('[data-continue-shopping]')) {
        continueShoppingButton.href = normalizeAuremeContinueShoppingUrl(source.auremeCartReferrer);
      }
    },

    applyAuremeCartPointsFromApi() {
      if (typeof Shopify !== 'undefined' && Shopify.designMode) return Promise.resolve();

      if (!this.isAuremeCartPage()) return Promise.resolve();

      const rawId =
        typeof AUREME_CUSTOMER_ID !== 'undefined' && AUREME_CUSTOMER_ID !== null
          ? String(AUREME_CUSTOMER_ID).trim()
          : '';
      if (!rawId) return Promise.resolve();

      const pointCache = window.AuremePointCache;
      if (
        !pointCache ||
        typeof SANYO_APP_PROXY_PATH === 'undefined' ||
        !SANYO_APP_PROXY_PATH ||
        typeof AUREME_STORE_DOMAIN === 'undefined' ||
        !AUREME_STORE_DOMAIN
      ) {
        return Promise.resolve();
      }

      return pointCache.fetchPointAndPersistLocalStorage(rawId).then((result) => {
        const balanceEl = document.querySelector('[data-cart-points-balance]');
        const { pointsError } = this.getAuremeCartElements();

        const showBalanceFetchFailure = () => {
          if (balanceEl) balanceEl.textContent = '-';
          this.showAuremePointsAlert(pointsError, AUREME_POINTS_SYSTEM_ERROR_MESSAGE);
        };

        if (!result || result.point == null) {
          showBalanceFetchFailure();
          return;
        }

        const balance = Number(result.point);
        if (!Number.isFinite(balance) || balance < 0) {
          showBalanceFetchFailure();
          return;
        }

        if (balanceEl) balanceEl.textContent = formatAuremeNumber(balance);

        const totalsElement = document.querySelector('[data-cart-totals]');
        if (totalsElement) totalsElement.dataset.cartBalance = String(balance);

        const pointsInput = document.querySelector('[data-cart-points-input]');
        if (pointsInput) pointsInput.dataset.max = String(balance);

        this.invalidateAuremeElementCache();
        const {
          totalsElement: te,
          totalValueElement,
          cartTotal,
          pointsInput: pi,
          pointsHidden,
        } = this.getAuremeCartElements();

        if (!te || !pi || !pointsHidden) return;

        if (pointsError) pointsError.classList.add('hidden');

        let previousApplied = normalizeAuremeInt(parseInt(pointsHidden.value || '0', 10));
        if (previousApplied <= 0 && auremePointReservationActive()) {
          const cid = getAuremeCustomerIdString();
          const fromLs = readAuremeAppliedPointsFromLocalStorage(cid);
          if (fromLs > 0) previousApplied = fromLs;
        }
        if (auremePointReservationActive() && previousApplied > 0) {
          this.setAuremePointsValue(
            pi,
            pointsHidden,
            totalValueElement,
            cartTotal,
            Math.min(previousApplied, cartTotal),
          );
          return;
        }

        const appliedPoints = this.getValidAuremePoints(previousApplied, balance, cartTotal);

        if (appliedPoints !== previousApplied) {
          this.setAuremePointsValue(pi, pointsHidden, totalValueElement, cartTotal, appliedPoints);
          return this.updateAuremeAttributes(this.collectAuremeAttributes(), {
            preferJsOnly: true,
          }).catch((err) => {
            console.error(err);
          });
        }
      });
    },

    hydrateAuremePointSessionFromCartDom() {
      const customerId = getAuremeCustomerIdString();
      if (!customerId) return;

      const gift = (document.querySelector('[data-cart-points-giftcard]')?.value || '').trim();
      const applied = normalizeAuremeInt(
        parseInt(document.querySelector('[data-cart-points-hidden]')?.value || '0', 10),
      );

      if (gift && applied > 0) {
        localStorage.setItem(`sanyo_app_gift_card_code_${customerId}`, gift);
        localStorage.setItem(`sanyo_app_use_point_${customerId}`, applied.toLocaleString('ja-JP'));
      }
    },

    /**
     * 別ページ遷移後にカート HTML へ属性が載らない場合でも、sanyo/crest と同様 LS から DOM を復元する。
     */
    restoreAuremePointDomFromLocalStorage() {
      const customerId = getAuremeCustomerIdString();
      if (!customerId) return;

      const giftLs = (localStorage.getItem(`sanyo_app_gift_card_code_${customerId}`) || '').trim();
      if (!giftLs) return;

      const lsApplied = readAuremeAppliedPointsFromLocalStorage(customerId);
      if (lsApplied <= 0) return;

      const giftEl = document.querySelector('[data-cart-points-giftcard]');
      const pointsHidden = document.querySelector('[data-cart-points-hidden]');
      if (!giftEl || !pointsHidden) return;

      const domGift = (giftEl.value || '').trim();
      const domApplied = normalizeAuremeInt(parseInt(pointsHidden.value || '0', 10));

      if (!domGift) giftEl.value = giftLs;
      if (domApplied <= 0) pointsHidden.value = String(lsApplied);
    },

    async resetAuremePointReservationUI(customerId) {
      localStorage.removeItem(`sanyo_app_cart_token_${customerId}`);
      localStorage.removeItem(`sanyo_app_point_use_date_${customerId}`);
      localStorage.removeItem(`sanyo_app_gift_card_code_${customerId}`);
      localStorage.removeItem(`sanyo_app_use_point_${customerId}`);

      const giftEl = document.querySelector('[data-cart-points-giftcard]');
      if (giftEl) giftEl.value = '';

      this.invalidateAuremeElementCache();
      const {
        totalsElement,
        totalValueElement,
        cartTotal,
        pointsInput,
        pointsHidden,
        pointsError,
      } = this.getAuremeCartElements();

      if (pointsError) pointsError.classList.add('hidden');

      if (totalsElement && pointsInput && pointsHidden) {
        this.setAuremePointsValue(pointsInput, pointsHidden, totalValueElement, cartTotal, 0);
      }

      await this.updateAuremeAttributes(this.collectAuremeAttributes(), { preferJsOnly: true });
    },

    async syncAuremePointUsingStateFromApi() {
      if (typeof Shopify !== 'undefined' && Shopify.designMode) return;
      if (!this.isAuremeCartPage()) return;

      const customerId = getAuremeCustomerIdString();
      if (!customerId) return;

      if (
        typeof SANYO_APP_PROXY_PATH === 'undefined' ||
        !SANYO_APP_PROXY_PATH ||
        typeof AUREME_STORE_DOMAIN === 'undefined' ||
        !AUREME_STORE_DOMAIN
      ) {
        return;
      }

      const giftFromDom = (
        document.querySelector('[data-cart-points-giftcard]')?.value || ''
      ).trim();
      const giftFromLs = (
        localStorage.getItem(`sanyo_app_gift_card_code_${customerId}`) || ''
      ).trim();
      const giftCardCode = giftFromDom || giftFromLs;

      if (!giftCardCode) {
        localStorage.removeItem(`sanyo_app_cart_token_${customerId}`);
        localStorage.removeItem(`sanyo_app_point_use_date_${customerId}`);
        localStorage.removeItem(`sanyo_app_gift_card_code_${customerId}`);
        localStorage.removeItem(`sanyo_app_use_point_${customerId}`);
        return;
      }

      const giftEl = document.querySelector('[data-cart-points-giftcard]');
      if (giftEl && !giftEl.value) giftEl.value = giftCardCode;

      try {
        const cartData = await fetch(`${routes.cart_url}.js`).then((response) => {
          if (!response.ok) throw new Error('cart.js failed');
          return response.json();
        });

        localStorage.setItem(
          `sanyo_app_subtotal_price_${customerId}`,
          String(cartData.items_subtotal_price / 100),
        );

        const cartTokenStored = localStorage.getItem(`sanyo_app_cart_token_${customerId}`);
        if (cartTokenStored && cartTokenStored !== cartData.token) {
          await this.resetAuremePointReservationUI(customerId);
          return;
        }

        const response = await fetch(
          `https://${AUREME_STORE_DOMAIN}${SANYO_APP_PROXY_PATH}/point/getlimitinfo`,
          {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ giftCardCode }),
          },
        );

        if (!response.ok) {
          console.error('[aureme-cart] getlimitinfo HTTP error');
          return;
        }

        const data = await response.json();
        if (auremePointErrorFlagIsSet(data.error_flg) || !data.enable_flg) {
          await this.resetAuremePointReservationUI(customerId);
        }
      } catch (error) {
        console.error(error);
      }
    },

    disconnectAuremeDeliveryAppLoadingObserver(wrapper) {
      auremeDeliveryAppLoadingHelpers.disconnect(wrapper);
    },

    hideAuremeDeliveryAppLoading(wrapper, loadingElement) {
      auremeDeliveryAppLoadingHelpers.hide(wrapper, loadingElement);
    },

    showAuremeDeliveryAppLoading(wrapper, loadingElement) {
      auremeDeliveryAppLoadingHelpers.show(wrapper, loadingElement);
    },

    cleanupAuremeDeliveryAppLoadingObservers() {
      auremeDeliveryAppLoadingHelpers.cleanup();
    },

    /* AMP配送日時アプリがフォーム本体を描画するまで、テーマ側のスピナーを表示する */
    syncAuremeDeliveryAppLoading() {
      auremeDeliveryAppLoadingHelpers.sync();
    },

    bindAuremeCartControls() {
      /* 同一更新サイクル中だけ DOM 参照を再利用し、HTML 差し替え後は都度捨てる */
      this.invalidateAuremeElementCache();
      this.hydrateAuremePointSessionFromCartDom();
      this.restoreAuremePointDomFromLocalStorage();
      this.applyAuremeContinueShoppingReferrer();
      const {
        totalsElement,
        totalValueElement,
        cartTotal,
        pointsBalance,
        pointsInput,
        pointsHidden,
        pointsError,
        pointsApplyButton,
      } = this.getAuremeCartElements();
      const deliveryDateSelect = document.querySelector('[data-cart-delivery-date]');
      const deliveryTimeSelect = document.querySelector('[data-cart-delivery-time]');

      buildAuremeDeliveryDateOptions(deliveryDateSelect);
      this.syncAuremeDeliveryAppLoading();

      if (totalsElement && pointsInput && pointsHidden) {
        let rawApplied = normalizeAuremeInt(
          parseInt(pointsHidden.value || totalsElement.dataset.cartApplied || '0', 10),
        );
        if (rawApplied <= 0 && auremePointReservationActive()) {
          const cid = getAuremeCustomerIdString();
          const fromLs = readAuremeAppliedPointsFromLocalStorage(cid);
          if (fromLs > 0) rawApplied = fromLs;
        }
        const appliedPoints =
          rawApplied > 0 && auremePointReservationActive()
            ? Math.min(rawApplied, cartTotal)
            : this.getValidAuremePoints(rawApplied, pointsBalance, cartTotal);

        this.setAuremePointsValue(
          pointsInput,
          pointsHidden,
          totalValueElement,
          cartTotal,
          appliedPoints,
        );

        if (pointsError) pointsError.classList.add('hidden');

        if (pointsInput.dataset.bound !== 'true') {
          pointsInput.addEventListener('input', () => {
            const raw = (pointsInput.value || '').replace(/[^0-9]/g, '');
            const num = parseInt(raw, 10);
            if (!raw || Number.isNaN(num)) {
              pointsInput.value = '';
              return;
            }
            pointsInput.value = formatAuremeNumber(num);
          });
          pointsInput.dataset.bound = 'true';
        }

        if (pointsApplyButton && pointsApplyButton.dataset.bound !== 'true') {
          pointsApplyButton.addEventListener('click', () => {
            if (pointsApplyButton.dataset.mode === 'clear') {
              void this.clearAuremePoints();
              return;
            }

            void this.applyAuremePoints();
          });
          pointsApplyButton.dataset.bound = 'true';
        }
      } else if (totalsElement) {
        this.updateAuremeTotalDisplay(totalValueElement, cartTotal, 0);
      }

      for (const selectElement of [deliveryDateSelect, deliveryTimeSelect]) {
        if (!selectElement || selectElement.dataset.bound === 'true') continue;

        selectElement.addEventListener('change', () => {
          this.updateAuremeAttributes(this.collectAuremeAttributes()).catch((error) => {
            console.error(error);
            window.AuremeToast?.show({
              message: '配送指定の更新に失敗しました。\nページを再読み込みしてお試しください。',
              type: 'error',
            });
          });
        });
        selectElement.dataset.bound = 'true';
      }

      this.syncAuremeHiddenDeliveryAttributes();
      this.syncAuremeCartRestrictions();
      this.bindAuremeCheckoutGuard();
      this.syncAuremeCheckoutCouponDisplay();

      if (this.isAuremeCartPage()) {
        void this.syncAuremePointUsingStateFromApi().then(() =>
          this.applyAuremeCartPointsFromApi(),
        );
      }
    },

    /*
     * 配送指定 UI を無効化した後もカート属性に古い指定日時が残ると
     * そのまま checkout へ引き継がれるため、非表示時は空文字で明示的に消す。
     */
    syncAuremeHiddenDeliveryAttributes() {
      if (!this.isAuremeCartPage() || this.tagName !== 'CART-ITEMS') return;

      const { footerStateElement, deliveryOptionsEnabled, storedDeliveryDate, storedDeliveryTime } =
        this.getAuremeFooterState();

      if (!footerStateElement || deliveryOptionsEnabled || this.isAuremeDeliveryAttributesSyncing)
        return;
      if (storedDeliveryDate === '' && storedDeliveryTime === '') return;

      const attributes = {
        ...this.collectAuremeAttributes(),
        配送指定日: '',
        配送時間指定: '',
      };

      this.isAuremeDeliveryAttributesSyncing = true;
      this.updateAuremeAttributes(attributes)
        .catch((error) => {
          console.error(error);
        })
        .finally(() => {
          this.isAuremeDeliveryAttributesSyncing = false;
        });
    },

    async prepareAuremeDeliveryAttributesForCheckout() {
      if (
        this.isAuremeCartPage() ||
        this.tagName !== 'CART-DRAWER-ITEMS' ||
        this.isAuremeDeliveryAttributesSyncing
      ) {
        return;
      }

      /* カート状態を一度だけ取得し、recordAuremeCheckoutContextと共有する */
      const cartState = await AuremeCheckoutUtils.fetchCartState();
      this._lastCartState = cartState;
      await AuremeCheckoutUtils.prepareDeliveryAttributesForCheckout({
        cartState,
        updateAttributes: (attributes) => this.updateAuremeAttributes(attributes),
        isSyncing: this.isAuremeDeliveryAttributesSyncing,
        setSyncing: (isSyncing) => {
          this.isAuremeDeliveryAttributesSyncing = isSyncing;
        },
      });
    },

    getValidAuremePoints(value, balance, total) {
      const parsedValue = parseInt(value || '0', 10);
      if (Number.isNaN(parsedValue) || parsedValue < 0) return 0;
      return Math.min(parsedValue, balance, total);
    },

    updateAuremeTotalDisplay(totalValueElement, cartTotal, appliedPoints) {
      if (!totalValueElement) return;

      const adjustedTotal = Math.max(0, cartTotal - appliedPoints);
      totalValueElement.textContent = formatAuremeYen(adjustedTotal);
    },

    updateAuremePointsStateClass(element, defaultClassName, appliedClassName, hasAppliedPoints) {
      if (!element) return;

      element.classList.toggle(defaultClassName, !hasAppliedPoints);
      element.classList.toggle(appliedClassName, hasAppliedPoints);
    },

    updateAuremePointsButtonState(buttonElement, appliedPoints) {
      if (!buttonElement) return;

      const hasAppliedPoints = normalizeAuremeInt(appliedPoints) > 0;
      buttonElement.classList.toggle('aureme-points__button--apply', !hasAppliedPoints);
      buttonElement.classList.toggle('aureme-points__button--clear', hasAppliedPoints);
      buttonElement.dataset.mode = hasAppliedPoints ? 'clear' : 'apply';
      buttonElement.textContent = hasAppliedPoints
        ? AUREME_POINTS_CLEAR_LABEL
        : AUREME_POINTS_APPLY_LABEL;
    },

    updateAuremePointsUiState(pointsBalanceElement, pointsInput, buttonElement, appliedPoints) {
      const hasAppliedPoints = normalizeAuremeInt(appliedPoints) > 0;

      this.updateAuremePointsStateClass(
        pointsBalanceElement,
        'aureme-points__balance',
        'aureme-points__balance--applied',
        hasAppliedPoints,
      );
      this.updateAuremePointsStateClass(
        pointsInput,
        'aureme-points__input',
        'aureme-points__input--applied',
        hasAppliedPoints,
      );
      if (pointsInput) {
        pointsInput.disabled = hasAppliedPoints;
      }
      this.updateAuremePointsButtonState(buttonElement, appliedPoints);
    },

    collectAuremeAttributes() {
      return {
        [AUREME_POINT_GIFT_CARD_ATTRIBUTE]:
          document.querySelector('[data-cart-points-giftcard]')?.value || '',
        配送指定日: document.querySelector('[data-cart-delivery-date]')?.value || '',
        配送時間指定: document.querySelector('[data-cart-delivery-time]')?.value || '',
      };
    },

    setAuremePointsValue(pointsInput, pointsHidden, totalValueElement, cartTotal, points) {
      const { pointsBalanceElement, pointsApplyButton } = this.getAuremeCartElements();
      const hasPoints = points > 0;
      pointsHidden.value = hasPoints ? String(points) : '';
      pointsInput.value = hasPoints ? formatAuremeNumber(points) : '';
      this.updateAuremeTotalDisplay(totalValueElement, cartTotal, points);
      this.updateAuremePointsUiState(pointsBalanceElement, pointsInput, pointsApplyButton, points);
    },

    showAuremePointsError(pointsError, message) {
      if (!pointsError) return;
      pointsError.textContent = message;
      pointsError.classList.remove('hidden');
    },

    showAuremePointsErrorHtml(pointsError, html) {
      if (!pointsError) return;
      pointsError.innerHTML = html;
      pointsError.classList.remove('hidden');
    },

    focusAuremePointsCheckoutNotice() {
      const footer = document.getElementById('main-cart-footer');
      if (footer) {
        footer.scrollIntoView({ behavior: 'smooth', block: 'start' });
        return true;
      }
      return false;
    },

    validateAuremePointsBeforeCheckout() {
      const { pointsInput, pointsHidden, pointsError } = this.getAuremeCartElements();
      if (!pointsInput || !pointsHidden) return true;

      const appliedPoints = normalizeAuremeInt(parseInt(pointsHidden.value || '0', 10));
      if (appliedPoints > 0 || auremePointReservationActive()) {
        return true;
      }

      const rawInput = (pointsInput.value || '').replace(/,/g, '').trim();
      if (rawInput == 0) {
        if (pointsError) pointsError.classList.add('hidden');
        return true;
      }

      this.showAuremePointsErrorHtml(pointsError, AUREME_POINTS_APPLY_REQUIRED_MESSAGE);
      this.focusAuremePointsCheckoutNotice();
      return false;
    },

    showAuremePointsAlert(pointsError, message) {
      if (pointsError) pointsError.classList.add('hidden');
      alert(message);
    },

    async applyAuremePoints() {
      const {
        totalsElement,
        totalValueElement,
        cartTotal,
        pointsBalance,
        pointsInput,
        pointsHidden,
        pointsError,
        pointsApplyButton,
      } = this.getAuremeCartElements();

      if (!totalsElement || !pointsInput || !pointsHidden) return;

      const customerId = getAuremeCustomerIdString();
      if (!customerId) {
        this.showAuremePointsError(pointsError, 'ログイン情報を確認できませんでした。');
        return;
      }

      if (
        typeof SANYO_APP_PROXY_PATH === 'undefined' ||
        !SANYO_APP_PROXY_PATH ||
        typeof AUREME_STORE_DOMAIN === 'undefined' ||
        !AUREME_STORE_DOMAIN
      ) {
        this.showAuremePointsError(pointsError, 'ポイント機能の設定を確認できませんでした。');
        return;
      }

      const rawDigits = (pointsInput.value || '').replace(/,/g, '');
      const isDigitsOnly = /^[0-9]+$/.test(rawDigits);
      const requestedPoints = parseInt(rawDigits, 10);

      if (!isDigitsOnly || Number.isNaN(requestedPoints) || requestedPoints <= 0) {
        this.showAuremePointsError(pointsError, 'ポイントは半角数字で入力してください。');
        return;
      }

      if (requestedPoints > AUREME_POINT_USE_MAX) {
        this.showAuremePointsError(
          pointsError,
          'ポイント利用可能上限は1,000,000ポイントまでです。',
        );
        return;
      }

      if (pointsBalance <= 0) {
        this.showAuremePointsError(pointsError, '利用できるポイントは0ポイントです。');
        return;
      }

      if (requestedPoints > pointsBalance) {
        this.showAuremePointsError(
          pointsError,
          `利用できるポイントは${formatAuremeNumber(pointsBalance)}ポイントです。`,
        );
        return;
      }

      if (requestedPoints > cartTotal) {
        this.showAuremePointsError(pointsError, 'ご注文金額を超えるポイントは利用できません。');
        return;
      }

      if (pointsApplyButton) pointsApplyButton.disabled = true;

      try {
        if (pointsError) pointsError.classList.add('hidden');

        const giftCardCode = await fetchAuremePointUseApi(requestedPoints);

        localStorage.setItem(
          `sanyo_app_use_point_${customerId}`,
          requestedPoints.toLocaleString('ja-JP'),
        );
        localStorage.setItem(`sanyo_app_gift_card_code_${customerId}`, giftCardCode);

        const giftEl = document.querySelector('[data-cart-points-giftcard]');
        if (giftEl) giftEl.value = giftCardCode;

        this.setAuremePointsValue(
          pointsInput,
          pointsHidden,
          totalValueElement,
          cartTotal,
          requestedPoints,
        );

        await this.updateAuremeAttributes(this.collectAuremeAttributes(), { preferJsOnly: true });

        const pointCache = window.AuremePointCache;
        if (pointCache?.fetchPointAndPersistLocalStorage) {
          await pointCache.fetchPointAndPersistLocalStorage(customerId);
        }
        void this.applyAuremeCartPointsFromApi();
      } catch (error) {
        console.error(error);
        this.showAuremePointsAlert(pointsError, resolveAuremePointsAlertMessage(error));
      } finally {
        const btn = document.querySelector('[data-cart-points-apply]');
        if (btn) btn.disabled = false;
      }
    },

    async clearAuremePoints() {
      const {
        totalsElement,
        totalValueElement,
        cartTotal,
        pointsInput,
        pointsHidden,
        pointsError,
        pointsApplyButton,
      } = this.getAuremeCartElements();

      if (!totalsElement || !pointsInput || !pointsHidden) return;

      const customerId = getAuremeCustomerIdString();

      if (pointsError) pointsError.classList.add('hidden');

      const giftEl = document.querySelector('[data-cart-points-giftcard]');
      const giftCard = (
        giftEl?.value ||
        (customerId ? localStorage.getItem(`sanyo_app_gift_card_code_${customerId}`) : '') ||
        ''
      ).trim();

      let usePointFormatted = customerId
        ? localStorage.getItem(`sanyo_app_use_point_${customerId}`)
        : '';
      if (!usePointFormatted) {
        const n = normalizeAuremeInt(parseInt(pointsHidden.value || '0', 10));
        usePointFormatted = n > 0 ? n.toLocaleString('ja-JP') : '';
      }

      if (pointsApplyButton) pointsApplyButton.disabled = true;

      try {
        if (
          giftCard &&
          typeof SANYO_APP_PROXY_PATH !== 'undefined' &&
          SANYO_APP_PROXY_PATH &&
          typeof AUREME_STORE_DOMAIN !== 'undefined' &&
          AUREME_STORE_DOMAIN
        ) {
          await fetchAuremePointCancelApi(giftCard, usePointFormatted || '');
        }

        if (customerId) {
          localStorage.removeItem(`sanyo_app_gift_card_code_${customerId}`);
          localStorage.removeItem(`sanyo_app_cart_token_${customerId}`);
          localStorage.removeItem(`sanyo_app_point_use_date_${customerId}`);
          localStorage.removeItem(`sanyo_app_use_point_${customerId}`);
        }

        if (giftEl) giftEl.value = '';

        this.setAuremePointsValue(pointsInput, pointsHidden, totalValueElement, cartTotal, 0);

        await this.updateAuremeAttributes(this.collectAuremeAttributes(), { preferJsOnly: true });

        if (customerId && window.AuremePointCache?.fetchPointAndPersistLocalStorage) {
          await window.AuremePointCache.fetchPointAndPersistLocalStorage(customerId);
        }
        void this.applyAuremeCartPointsFromApi();
      } catch (error) {
        console.error(error);
        this.showAuremePointsAlert(pointsError, resolveAuremePointsAlertMessage(error));
      } finally {
        const btn = document.querySelector('[data-cart-points-apply]');
        if (btn) btn.disabled = false;
      }
    },

    updateAuremeAttributes(attributes, options = {}) {
      const attributesClearingPointAmount =
        AuremeCheckoutUtils.buildCartAttributesClearingPointAmount(attributes);
      if (options.preferJsOnly) {
        const jsBody = JSON.stringify({ attributes: attributesClearingPointAmount });
        return fetch(`${routes.cart_update_url}.js`, {
          ...fetchConfig(),
          body: jsBody,
        })
          .then((response) => {
            if (!response.ok) {
              throw new Error(`cart_update.js failed: ${response.status}`);
            }
            return response.json();
          })
          .then((cart) => {
            persistAuremePointCartSnapshotFromCartJson(cart);
            this.syncAuremeCartDomFromCartJson(cart);
            this.invalidateAuremeElementCache();
            this.bindAuremeCartControls();
            return cart;
          });
      }

      const body = JSON.stringify({
        attributes: attributesClearingPointAmount,
        sections: this.getSectionsToRender().map((section) => section.section),
        sections_url: window.location.pathname,
      });

      return fetch(routes.cart_update_url, { ...fetchConfig(), body })
        .then((response) => response.text())
        .then((state) => {
          const parsedState = JSON.parse(state);

          if (!parsedState?.sections) {
            throw new Error('cart_update response does not include sections');
          }

          this.classList.toggle('is-empty', parsedState.item_count === 0);
          this.updateAuremeCartPageEmptyState(parsedState.item_count);
          const cartFooter = document.getElementById('main-cart-footer');

          if (cartFooter) cartFooter.classList.toggle('is-empty', parsedState.item_count === 0);

          for (const section of this.getSectionsToRender()) {
            const sectionRoot = document.getElementById(section.id);
            if (!sectionRoot) continue;
            const sectionHtml = parsedState.sections[section.section];
            if (!sectionHtml) continue;

            const elementToReplace = sectionRoot.querySelector(section.selector) || sectionRoot;
            elementToReplace.innerHTML = this.getSectionInnerHTML(sectionHtml, section.selector);
          }

          this.invalidateAuremeElementCache();

          const cid = getAuremeCustomerIdString();
          const persistPromise = cid ? persistAuremeCartTokenForPoints(cid) : Promise.resolve();
          return persistPromise.then(() => {
            this.bindAuremeCartControls();
            return parsedState;
          });
        })
        .catch((error) => {
          console.error(error);

          const fallbackBody = JSON.stringify({ attributes: attributesClearingPointAmount });
          return fetch(`${routes.cart_update_url}.js`, {
            ...fetchConfig(),
            body: fallbackBody,
          })
            .then((response) => {
              if (!response.ok) {
                throw new Error(`cart_update fallback failed: ${response.status}`);
              }
              return response.json();
            })
            .then((cart) => {
              persistAuremePointCartSnapshotFromCartJson(cart);
              this.syncAuremeCartDomFromCartJson(cart);
              this.invalidateAuremeElementCache();
              this.bindAuremeCartControls();
              return cart;
            });
        });
    },

    syncAuremeCartDomFromCartJson(cart) {
      if (!cart || typeof cart !== 'object') return;

      const giftEl = document.querySelector('[data-cart-points-giftcard]');
      const pointsHidden = document.querySelector('[data-cart-points-hidden]');
      const appliedFromDom = normalizeAuremeInt(parseInt(pointsHidden?.value || '0', 10));
      const pointSyncState = deriveAuremePointSyncState(cart, appliedFromDom);
      const syncedAppliedValue =
        pointSyncState.syncedAppliedYen > 0 ? String(pointSyncState.syncedAppliedYen) : '';

      const totalsElement = document.querySelector('[data-cart-totals]');
      if (totalsElement) {
        totalsElement.dataset.cartTotal = String(pointSyncState.cartTotalBaseYen);
        totalsElement.dataset.cartApplied = syncedAppliedValue;
      }

      if (giftEl && pointSyncState.hasCartGiftAttribute) {
        giftEl.value = pointSyncState.cartGiftString;
      }

      if (pointsHidden) {
        pointsHidden.value =
          pointSyncState.shouldKeepPointValue && syncedAppliedValue ? syncedAppliedValue : '';
      }

      if (typeof cart.item_count === 'number') {
        this.classList.toggle('is-empty', cart.item_count === 0);
        this.updateAuremeCartPageEmptyState(cart.item_count);
        const cartFooter = document.getElementById('main-cart-footer');
        if (cartFooter) cartFooter.classList.toggle('is-empty', cart.item_count === 0);
      }
    },
  };

  window.AuremeCartEnhancements = {
    extendCartItems(CartItemsClass) {
      if (!CartItemsClass || CartItemsClass.prototype.auremeCartEnhancementsApplied) return;

      Object.assign(CartItemsClass.prototype, auremeCartMethods);
      CartItemsClass.prototype.auremeCartEnhancementsApplied = true;
    },
  };
})();
